/**
 * Firestore read paths for ASI canvas / dashboards.
 * Institution-scoped reads rely on existing asi* security rules (server-write-only).
 */
import { db } from "services/firebase";
import {
  buildAccessByNodeId,
  resolvePermissionsFromAssignments,
} from "../utils/asiRoleResolverClient";
import { listPendingCandidates } from "./asiManagerService";

const COLLECTIONS = {
  workflows: "asiWorkflows",
  interactions: "asiInteractions",
  roleAssignments: "asiRoleAssignments",
  businessUnits: "asiBusinessUnits",
  trainerFeedback: "asiTrainerFeedback",
  users: "users",
  localPrompts: "llmLocalPrompts",
  globalPrompts: "llmGlobalPrompts",
  pendingQueues: "asiWorkflowPendingQueues",
  graphCandidates: "asiGraphCandidates",
  staffAssignments: "asiStaffAssignments",
};

/** Data-model default when asiBusinessUnits.attributionMode is unset. */
export const DEFAULT_ATTRIBUTION_MODE = "named";

export async function loadAsiWorkflow(workflowId) {
  const id = String(workflowId || "").trim();
  if (!id) return null;
  const snap = await db.collection(COLLECTIONS.workflows).doc(id).get();
  if (!snap.exists) return null;
  return { workflowId: snap.id, ...snap.data() };
}

/**
 * One-shot catalog fetch for library templates (D2 rules: isTemplate == true).
 * @returns {Promise<Array<object>>}
 */
export async function listAsiWorkflowTemplates() {
  const snap = await db
    .collection(COLLECTIONS.workflows)
    .where("isTemplate", "==", true)
    .get();
  return snap.docs.map((doc) => ({
    workflowId: doc.id,
    ...doc.data(),
  }));
}

export async function loadAsiInteractionsByIds(interactionIds = []) {
  const ids = [...new Set((interactionIds || []).filter(Boolean))];
  const byId = {};
  await Promise.all(
    ids.map(async (id) => {
      try {
        const snap = await db.collection(COLLECTIONS.interactions).doc(id).get();
        if (snap.exists) {
          byId[id] = { interactionId: id, ...snap.data() };
        }
      } catch {
        // Out-of-scope Interaction (e.g. uncovered no-IM node) — skip.
      }
    }),
  );
  return byId;
}

export async function loadAsiRoleAssignmentsForUid(uid) {
  const userId = String(uid || "").trim();
  if (!userId) return [];
  const snap = await db
    .collection(COLLECTIONS.roleAssignments)
    .where("uid", "==", userId)
    .get();
  return snap.docs.map((doc) => ({ assignmentId: doc.id, ...doc.data() }));
}

/**
 * Own ASI Staff grant (`asiStaffAssignments/{uid}`). Doc id is the uid.
 * @returns {Promise<object|null>}
 */
export async function loadAsiStaffAssignmentForUid(uid) {
  const userId = String(uid || "").trim();
  if (!userId) return null;
  const snap = await db.collection(COLLECTIONS.staffAssignments).doc(userId).get();
  if (!snap.exists) return null;
  return { uid: snap.id, ...snap.data() };
}

/**
 * Interactions the caller may view/annotate as Trainer (or IM).
 * Uses rule-friendly interaction queries (institutionId / businessUnitId / get)
 * rather than workflow scopeKeys list queries.
 * @param {string} uid
 * @returns {Promise<Array<object>>}
 */
export async function listTrainerAssignedInteractions(uid) {
  const userId = String(uid || "").trim();
  if (!userId) return [];

  const assignments = await loadAsiRoleAssignmentsForUid(userId);
  if (!assignments.length) return [];

  const byId = {};

  for (const a of assignments) {
    if (a.scopeType === "org" && a.scopeId) {
      const snap = await db
        .collection(COLLECTIONS.interactions)
        .where("institutionId", "==", a.scopeId)
        .get();
      snap.docs.forEach((doc) => {
        byId[doc.id] = { interactionId: doc.id, ...doc.data() };
      });
    } else if (a.scopeType === "businessUnit" && a.scopeId) {
      const snap = await db
        .collection(COLLECTIONS.interactions)
        .where("businessUnitId", "==", a.scopeId)
        .get();
      snap.docs.forEach((doc) => {
        byId[doc.id] = { interactionId: doc.id, ...doc.data() };
      });
    } else if (a.scopeType === "interaction" && a.scopeId) {
      const snap = await db
        .collection(COLLECTIONS.interactions)
        .doc(a.scopeId)
        .get();
      if (snap.exists) {
        byId[snap.id] = { interactionId: snap.id, ...snap.data() };
      }
    }
  }

  const rows = [];
  for (const interaction of Object.values(byId)) {
    const perms = resolvePermissionsFromAssignments(assignments, interaction);
    if (!perms.canViewAnnotate) continue;
    rows.push({
      ...interaction,
      displayName:
        interaction.displayName ||
        interaction.label ||
        interaction.interactionId,
    });
  }

  rows.sort((a, b) =>
    String(a.displayName || a.interactionId).localeCompare(
      String(b.displayName || b.interactionId),
    ),
  );
  return rows;
}

/**
 * Resolve attribution mode for a business unit.
 * Spec default when unset / missing BU doc: "named" (ASI_INTERACTION_DATA_MODEL.md).
 */
export async function loadAttributionMode(businessUnitId) {
  const buId = String(businessUnitId || "").trim();
  if (!buId) return DEFAULT_ATTRIBUTION_MODE;
  const snap = await db.collection(COLLECTIONS.businessUnits).doc(buId).get();
  if (!snap.exists) return DEFAULT_ATTRIBUTION_MODE;
  return snap.data()?.attributionMode === "anonymized"
    ? "anonymized"
    : DEFAULT_ATTRIBUTION_MODE;
}

export async function loadTrainerFeedbackForInteraction(interactionId) {
  const id = String(interactionId || "").trim();
  if (!id) return [];
  const snap = await db
    .collection(COLLECTIONS.trainerFeedback)
    .where("interactionId", "==", id)
    .get();

  const rows = snap.docs.map((doc) => ({
    id: doc.id,
    feedbackId: doc.id,
    ...doc.data(),
  }));

  // Newest first when timestamps exist
  rows.sort((a, b) => {
    const aMs = a.createdAt?.toMillis?.() || a.createdAt?.seconds * 1000 || 0;
    const bMs = b.createdAt?.toMillis?.() || b.createdAt?.seconds * 1000 || 0;
    return bMs - aMs;
  });

  const trainerUids = [
    ...new Set(rows.map((r) => r.trainerUid).filter(Boolean)),
  ];
  const nameByUid = {};
  await Promise.all(
    trainerUids.map(async (uid) => {
      try {
        const userSnap = await db.collection(COLLECTIONS.users).doc(uid).get();
        if (!userSnap.exists) return;
        const data = userSnap.data() || {};
        nameByUid[uid] =
          data.displayName ||
          data.name ||
          [data.firstName, data.lastName].filter(Boolean).join(" ") ||
          data.email ||
          uid;
      } catch {
        // User doc may be unreadable; fall back to uid in the UI.
      }
    }),
  );

  return rows.map((row) => ({
    ...row,
    uid: row.trainerUid || null,
    trainerName: nameByUid[row.trainerUid] || null,
    displayName: nameByUid[row.trainerUid] || null,
    feedback: row.text || row.feedback || row.content || "",
    text: row.text || row.feedback || row.content || "",
  }));
}

async function loadActivePromptSnapshot(promptId, organizationId) {
  const pid = String(promptId || "").trim();
  if (!pid) return null;

  if (organizationId) {
    const localId = `org__${organizationId}__${pid}`;
    try {
      const localSnap = await db.collection(COLLECTIONS.localPrompts).doc(localId).get();
      if (localSnap.exists) {
        const data = localSnap.data() || {};
        const content =
          data.organizationPrompt || data.prompt || data.regionalPrompt || "";
        if (content) {
          return { promptId: pid, content, source: "org" };
        }
      }
    } catch {
      // fall through to global
    }
  }

  try {
    const globalSnap = await db.collection(COLLECTIONS.globalPrompts).doc(pid).get();
    if (globalSnap.exists) {
      const data = globalSnap.data() || {};
      return {
        promptId: pid,
        content: data.systemPrompt || data.globalPrompt || data.prompt || "",
        source: "global",
      };
    }
  } catch {
    // ignore
  }

  return { promptId: pid, content: "", source: null };
}

/**
 * Derive per-node health from topology + pending queue + deferred feedback + graph touch.
 * Priority: prompt/graph pending > deferred-no-manager > live(healthy) > design.
 *
 * @param {Array} nodes
 * @param {Record<string, object[]>} pendingByInteraction
 * @param {string[]} [touchedNodeIds] graph-candidate touched node ids
 * @param {Record<string, object[]>} [deferredByInteraction] feedback deferred for no IM
 */
export function computeHealthByNodeId(
  nodes = [],
  pendingByInteraction = {},
  touchedNodeIds = [],
  deferredByInteraction = {},
) {
  const touched = new Set(
    (Array.isArray(touchedNodeIds) ? touchedNodeIds : []).filter(Boolean),
  );
  const healthByNodeId = {};
  for (const node of nodes) {
    if (!node?.nodeId) continue;
    if (
      pendingByInteraction[node.interactionId]?.length ||
      touched.has(node.nodeId)
    ) {
      healthByNodeId[node.nodeId] = "pending";
    } else if (deferredByInteraction[node.interactionId]?.length) {
      healthByNodeId[node.nodeId] = "deferred";
    } else if (node.status === "live") {
      healthByNodeId[node.nodeId] = "healthy";
    } else {
      healthByNodeId[node.nodeId] = "design";
    }
  }
  return healthByNodeId;
}

/**
 * Load open deferred trainer feedback (no promptCandidateId) keyed by interactionId.
 * Used for canvas health — distinct from pending prompt candidates.
 *
 * Pass institutionId when available so the query satisfies Firestore rules that
 * gate org-scoped Interaction Managers via resource.data.institutionId.
 *
 * @param {string[]} interactionIds
 * @param {{ institutionId?: string }} [opts]
 * @returns {Promise<Record<string, object[]>>}
 */
export async function loadDeferredFeedbackByInteraction(
  interactionIds = [],
  { institutionId } = {},
) {
  const ids = [...new Set((interactionIds || []).filter(Boolean))];
  const orgId = String(institutionId || "").trim();
  const deferredByInteraction = {};
  await Promise.all(
    ids.map(async (interactionId) => {
      try {
        let query = db
          .collection(COLLECTIONS.trainerFeedback)
          .where("interactionId", "==", interactionId);
        if (orgId) {
          query = query.where("institutionId", "==", orgId);
        }
        const snap = await query.get();
        const rows = snap.docs
          .map((doc) => ({ id: doc.id, feedbackId: doc.id, ...doc.data() }))
          .filter(
            (row) =>
              row.candidateDeferred === true &&
              !row.promptCandidateId &&
              (row.deferredReason === "no-active-interaction-manager" ||
                !row.deferredReason),
          );
        if (rows.length) deferredByInteraction[interactionId] = rows;
      } catch {
        // Rules may deny; leave empty for this interaction.
      }
    }),
  );
  return deferredByInteraction;
}

/**
 * Normalize pendingByInteraction map from a queue summary doc (or callable-shaped lists).
 */
export function normalizePendingByInteraction(raw) {
  if (!raw || typeof raw !== "object") return {};
  const out = {};
  for (const [ix, list] of Object.entries(raw)) {
    if (!ix || !Array.isArray(list)) continue;
    out[ix] = list.filter(Boolean);
  }
  return out;
}

/**
 * Normalize pendingGraphCandidates array from a queue summary doc.
 * @param {unknown} raw
 * @returns {object[]}
 */
export function normalizePendingGraphCandidates(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.filter((entry) => entry && (entry.candidateId || entry.id));
}

/**
 * One-shot load of asiGraphCandidates/{candidateId}.
 * @param {string} candidateId
 * @returns {Promise<object|null>}
 */
export async function loadAsiGraphCandidate(candidateId) {
  const id = String(candidateId || "").trim();
  if (!id) return null;
  const snap = await db.collection(COLLECTIONS.graphCandidates).doc(id).get();
  if (!snap.exists) return null;
  return { candidateId: snap.id, ...snap.data() };
}

/**
 * Live document listener for asiWorkflowPendingQueues/{workflowId}.
 * Returns an unsubscribe function. Missing docs call onChange with empty maps
 * (initial callable load remains the fallback until a summary exists).
 *
 * @param {string} workflowId
 * @param {(payload: {
 *   pendingByInteraction: Record<string, object[]>,
 *   pendingGraphCandidates: object[],
 *   exists: boolean,
 * }) => void} onChange
 * @param {(err: Error) => void} [onError]
 * @returns {() => void}
 */
export function subscribeWorkflowPendingQueue(workflowId, onChange, onError) {
  const id = String(workflowId || "").trim();
  if (!id || typeof onChange !== "function") {
    return () => {};
  }
  try {
    return db
      .collection(COLLECTIONS.pendingQueues)
      .doc(id)
      .onSnapshot(
        (snap) => {
          if (!snap.exists) {
            onChange({
              pendingByInteraction: {},
              pendingGraphCandidates: [],
              exists: false,
            });
            return;
          }
          const data = snap.data() || {};
          onChange({
            pendingByInteraction: normalizePendingByInteraction(
              data.pendingByInteraction,
            ),
            pendingGraphCandidates: normalizePendingGraphCandidates(
              data.pendingGraphCandidates,
            ),
            exists: true,
          });
        },
        (err) => {
          if (typeof onError === "function") onError(err);
        },
      );
  } catch (err) {
    if (typeof onError === "function") onError(err);
    return () => {};
  }
}

/**
 * Full canvas bootstrap: workflow graph, access map, pending queue, institution id.
 */
export async function loadWorkflowCanvasData({ workflowId, uid }) {
  const workflow = await loadAsiWorkflow(workflowId);
  if (!workflow) {
    return {
      workflow: null,
      accessByNodeId: {},
      healthByNodeId: {},
      pendingCandidates: [],
      interactionsById: {},
      assignments: [],
      institutionId: null,
    };
  }

  const nodes = Array.isArray(workflow.nodes) ? workflow.nodes : [];
  const interactionIds = nodes.map((n) => n.interactionId).filter(Boolean);
  const workflowInstitutionId =
    workflow.institutionId || workflow.organizationId || null;
  const [interactionsById, assignments, pendingResult, deferredByInteraction] =
    await Promise.all([
      loadAsiInteractionsByIds(interactionIds),
      loadAsiRoleAssignmentsForUid(uid),
      listPendingCandidates({
        institutionId: workflowInstitutionId || undefined,
      }).catch(() => ({ candidates: [] })),
      loadDeferredFeedbackByInteraction(interactionIds, {
        institutionId: workflowInstitutionId || undefined,
      }),
    ]);

  const pendingCandidates = (
    Array.isArray(pendingResult?.candidates)
      ? pendingResult.candidates
      : Array.isArray(pendingResult)
        ? pendingResult
        : []
  ).filter((c) => {
    const ix = c.interactionId;
    return !ix || interactionIds.includes(ix);
  });

  const pendingByInteraction = {};
  for (const c of pendingCandidates) {
    const ix = c.interactionId;
    if (!ix) continue;
    if (!pendingByInteraction[ix]) pendingByInteraction[ix] = [];
    pendingByInteraction[ix].push(c);
  }

  const accessByNodeId = buildAccessByNodeId({
    nodes,
    interactionsById,
    assignments,
  });

  const healthByNodeId = computeHealthByNodeId(
    nodes,
    pendingByInteraction,
    [],
    deferredByInteraction,
  );

  return {
    workflow: {
      ...workflow,
      workflowId: workflow.workflowId || workflowId,
      nodes,
      edges: Array.isArray(workflow.edges) ? workflow.edges : [],
    },
    accessByNodeId,
    healthByNodeId,
    pendingCandidates,
    pendingByInteraction,
    deferredByInteraction,
    interactionsById,
    assignments,
    institutionId: workflow.institutionId || null,
  };
}

/**
 * Enrich a selected node for the action drawer.
 */
export async function loadNodeActionContext({
  node,
  access,
  interaction,
  pendingForInteraction = [],
}) {
  const interactionId = node?.interactionId || interaction?.interactionId;
  const canApprove = Boolean(access?.canApprove);
  const organizationId =
    interaction?.institutionId || interaction?.organizationId || null;

  const [activePrompt, rawSubmissions, attributionMode] = await Promise.all([
    loadActivePromptSnapshot(interaction?.promptId || node?.promptId, organizationId),
    canApprove
      ? loadTrainerFeedbackForInteraction(interactionId)
      : Promise.resolve([]),
    loadAttributionMode(interaction?.businessUnitId),
  ]);

  return {
    activePrompt,
    rawSubmissions,
    attributionMode,
    pendingQueue: pendingForInteraction,
    feedbackSummary:
      pendingForInteraction[0]?.diffSummary ||
      pendingForInteraction[0]?.proposedPromptText ||
      null,
    candidate: pendingForInteraction[0] || null,
    candidateDeferred: (rawSubmissions || []).some(
      (row) =>
        row?.candidateDeferred === true &&
        !row?.promptCandidateId &&
        (row?.deferredReason === "no-active-interaction-manager" ||
          !row?.deferredReason),
    ),
  };
}

export { buildAccessByNodeId };
